import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import SidePanel, { SidePanelSection, SidePanelActions, SidePanelDivider } from '@/components/SidePanel';
import { API } from '@/App';
import { 
  Package, Plus, Search, Tag, ChevronRight, ChevronDown, Edit2, Trash2, 
  Eye, Clock, Check, X, Lock, MessageSquare, AlertTriangle, Upload, 
  Image, Filter, IndianRupee, Send, Loader2, Bell, Archive, RefreshCw,
  FileText, CheckCircle, XCircle, Sparkles, MessageCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

/**
 * VENDOR RATE SUBMISSION PAGE - NOTION-STYLE UI
 * 
 * Matches the Materials/PO tab layout pattern:
 * - Category-grouped list view
 * - Row-based structure with click-to-open
 * - Side panel for detail view/edit
 * - Consistent typography and spacing
 */

// Status configuration with colors matching the design system (NO fluorescent green)
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600', icon: FileText },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', icon: Clock },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  locked: { label: 'Locked (BOQ Ready)', color: 'bg-purple-100 text-purple-700', icon: Lock },
  amendment_requested: { label: 'Amendment Requested', color: 'bg-amber-100 text-amber-700', icon: MessageSquare },
  amended_pending_vendor_review: { label: 'Review Admin Changes', color: 'bg-orange-100 text-orange-700', icon: Eye },
  modified_by_admin: { label: 'Review Admin Changes', color: 'bg-orange-100 text-orange-700', icon: Eye },
  archived: { label: 'Archived', color: 'bg-stone-200 text-stone-500', icon: Archive },
  expired: { label: 'Expired', color: 'bg-stone-200 text-stone-500', icon: Clock },
};

// Amendment reasons
const AMENDMENT_REASONS = [
  { value: 'extend_validity', label: 'Extend Validity Period' },
  { value: 'incorrect_rate', label: 'Incorrect Rate' },
  { value: 'incorrect_description', label: 'Incorrect Description/Specification' },
  { value: 'incorrect_unit', label: 'Incorrect Unit' },
  { value: 'incorrect_gst', label: 'Incorrect GST' },
  { value: 'other', label: 'Other (Explain in comment)' },
];

// Validity options
const VALIDITY_OPTIONS = [15, 30, 45, 60, 90, 120, 180, 365];

// Unit options
const UNIT_OPTIONS = ['Sq.ft', 'Rft', 'Lft', 'No.s', 'Set', 'Pair', 'Kg', 'Ltr', 'Bag', 'Bundle', 'Box', 'Mtr', 'Sqm', 'Cum', 'Lot'];

export default function VendorRates() {
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [categories, setCategories] = useState({ standard_categories: [], custom_categories: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Side panel states
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [panelMode, setPanelMode] = useState('view'); // view, create, amendment
  const [selectedRate, setSelectedRate] = useState(null);
  const [loadingRate, setLoadingRate] = useState(false);
  
  // Create rate form
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryTemplate, setCategoryTemplate] = useState(null);
  const [formData, setFormData] = useState({
    material_name: '',
    description: '',
    unit: 'Sq.ft',
    rate: '',
    gst: 'extra',
    inclusion: 'material_only',
    exclusions: '',
    validity_days: '30',
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Amendment request
  const [amendmentForm, setAmendmentForm] = useState({
    reason: '',
    comment: '',
    requested_validity_days: '30',
    proposed_rate: '',
    proposed_description: '',
  });
  const [submittingAmendment, setSubmittingAmendment] = useState(false);
  
  // Photo upload
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  
  // AI-assisted description
  const [generatingAIDescription, setGeneratingAIDescription] = useState(false);
  
  // Request New Category dialog
  const [requestCategoryDialogOpen, setRequestCategoryDialogOpen] = useState(false);
  const [requestCategoryForm, setRequestCategoryForm] = useState({
    name: '',
    reason: '',
    suggested_units: [],
    trade_description: ''
  });
  const [submittingCategoryRequest, setSubmittingCategoryRequest] = useState(false);
  
  // AI suggestions for category request
  const [generatingCategorySuggestions, setGeneratingCategorySuggestions] = useState(false);
  const [categorySuggestions, setCategorySuggestions] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Start chat with admin about a specific rate item
  const handleStartChatAboutRate = async (rate) => {
    try {
      // Use the material name as the chat subject (e.g., "Aluminium Sliding Window")
      const subject = rate.material_name || rate.category_name || 'Rate Discussion';
      
      // Start or open chat with the subject
      const response = await axios.post(`${API}/vendor/chats/start`, {
        subject: subject
      });
      
      // Navigate to the chat page with the chat ID
      navigate(`/vendor/chats?chatId=${response.data.id}`);
      toast.success(`Chat opened: ${subject}`);
    } catch (error) {
      // If chat already exists, navigate to existing chat
      if (error.response?.status === 400 && error.response?.data?.chat_id) {
        navigate(`/vendor/chats?chatId=${error.response.data.chat_id}`);
      } else {
        console.error('Failed to start chat:', error);
        toast.error('Failed to open chat. Please try again.');
      }
    }
  };

  // Generate AI category suggestions
  const handleGenerateCategorySuggestions = async () => {
    if (!requestCategoryForm.name || requestCategoryForm.name.length < 2) {
      toast.error('Please enter a category name first');
      return;
    }
    
    setGeneratingCategorySuggestions(true);
    try {
      const response = await axios.post(`${API}/vendor/category-suggestions`, {
        name: requestCategoryForm.name
      });
      
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setCategorySuggestions(response.data.suggestions);
        toast.success('AI suggestions generated!');
      } else {
        toast.info('No suggestions found. Your category name looks unique!');
      }
    } catch (error) {
      console.error('Failed to get AI suggestions:', error);
      toast.error('Failed to generate suggestions');
    } finally {
      setGeneratingCategorySuggestions(false);
    }
  };

  // Apply AI suggestion to category form
  const applyCategorySuggestion = (suggestion) => {
    setRequestCategoryForm(prev => ({
      ...prev,
      name: suggestion.name || prev.name,
      reason: suggestion.reason || prev.reason,
      trade_description: suggestion.description || prev.trade_description
    }));
    setCategorySuggestions(null);
    toast.success('Suggestion applied! You can edit before submitting.');
  };

  // Generate AI-assisted description
  const handleGenerateAIDescription = async () => {
    if (!selectedCategory || !categoryTemplate) {
      toast.error('Please select a category first');
      return;
    }
    
    setGeneratingAIDescription(true);
    try {
      const response = await axios.post(`${API}/vendor/ai-description`, {
        category_id: selectedCategory,
        category_name: categoryTemplate.category_name,
        unit: formData.unit,
        material_name: formData.material_name,
        keywords: formData.description
      });
      
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          description: response.data.description
        }));
        toast.success(response.data.ai_assisted ? 'AI description generated' : 'Template description applied');
      }
    } catch (error) {
      toast.error('Failed to generate description');
    } finally {
      setGeneratingAIDescription(false);
    }
  };

  // Submit category request
  const handleSubmitCategoryRequest = async () => {
    if (!requestCategoryForm.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    if (!requestCategoryForm.reason.trim()) {
      toast.error('Please provide a reason for this category');
      return;
    }
    
    setSubmittingCategoryRequest(true);
    try {
      const response = await axios.post(`${API}/vendor/categories/request`, {
        name: requestCategoryForm.name.trim(),
        reason: requestCategoryForm.reason.trim(),
        suggested_units: requestCategoryForm.suggested_units.length > 0 
          ? requestCategoryForm.suggested_units 
          : ['Nos', 'Lumpsum'],
        trade_description: requestCategoryForm.trade_description.trim() || null
      });
      
      if (response.data.warnings?.length > 0) {
        toast.warning(response.data.warnings.join('. '));
      }
      
      toast.success('Category request submitted! Admin will review and approve.');
      setRequestCategoryDialogOpen(false);
      setRequestCategoryForm({ name: '', reason: '', suggested_units: [], trade_description: '' });
      fetchCategories(); // Refresh to show pending request
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit category request');
    } finally {
      setSubmittingCategoryRequest(false);
    }
  };

  // Fetch vendor's rates
  const fetchRates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vendor/my-rates`);
      // Handle new response structure with rates array
      const ratesData = response.data.rates || response.data;
      setRates(Array.isArray(ratesData) ? ratesData : []);
    } catch (error) {
      toast.error('Failed to load rates');
      setRates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vendor/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  }, []);

  useEffect(() => {
    fetchRates();
    fetchCategories();
  }, [fetchRates, fetchCategories]);

  // Get status badge
  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon size={12} />
        {config.label}
      </Badge>
    );
  };

  // Calculate expiry info
  const getExpiryInfo = (validityEnd) => {
    if (!validityEnd) return { isExpired: false, daysRemaining: 0 };
    const end = new Date(validityEnd);
    const now = new Date();
    const daysRemaining = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return {
      isExpired: daysRemaining <= 0,
      daysRemaining: Math.max(0, daysRemaining),
      isExpiringSoon: daysRemaining > 0 && daysRemaining <= 7
    };
  };

  // Separate active and archived rates
  const activeRates = rates.filter(r => !r.is_archived);
  const archivedRates = rates.filter(r => r.is_archived);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('active');

  // Group rates by category - based on active tab
  const ratesToDisplay = activeTab === 'active' ? activeRates : archivedRates;
  
  // P0 FIX: Use category_name (or category_id as fallback) for grouping
  const groupedRates = ratesToDisplay.reduce((acc, rate) => {
    const category = rate.category_name || rate.category_id || rate.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(rate);
    return acc;
  }, {});

  // Filter rates
  const filteredCategories = Object.keys(groupedRates).filter(category => {
    const categoryRates = groupedRates[category];
    return categoryRates.some(rate => {
      const matchesSearch = !searchQuery || 
        rate.material_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rate.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || rate.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  });

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Open rate detail panel
  const openRateDetail = async (rate) => {
    setSelectedRate(rate);
    setPanelMode('view');
    setPanelOpen(true);
    setLoadingRate(true);
    
    try {
      const response = await axios.get(`${API}/vendor/rates/${rate.id}`);
      setSelectedRate(response.data);
    } catch (error) {
      toast.error('Failed to load rate details');
    } finally {
      setLoadingRate(false);
    }
  };

  // Open create panel
  const openCreatePanel = () => {
    setSelectedCategory(null);
    setCategoryTemplate(null);
    setSearchQuery(''); // Reset search when opening create panel
    setFormData({
      material_name: '',
      description: '',
      unit: 'Sq.ft',
      rate: '',
      gst: 'extra',
      inclusion: 'material_only',
      exclusions: '',
      validity_days: '30',
    });
    setPanelMode('create');
    setPanelOpen(true);
  };

  // Open amendment panel
  const openAmendmentPanel = (rate) => {
    setSelectedRate(rate);
    setAmendmentForm({
      reason: '',
      comment: '',
      requested_validity_days: '30',
      proposed_rate: rate.rate?.toString() || '',
      proposed_description: rate.description || '',
    });
    setPanelMode('amendment');
    setPanelOpen(true);
  };

  // Select category and load template
  const handleCategorySelect = async (categoryId) => {
    setSelectedCategory(categoryId);
    try {
      // FIX: Correct endpoint URL - was /vendor/rate-templates, should be /vendor/categories/{id}/template
      const response = await axios.get(`${API}/vendor/categories/${categoryId}/template`);
      setCategoryTemplate(response.data);
      setFormData(prev => ({
        ...prev,
        unit: response.data.allowed_units?.[0] || 'Sq.ft',
      }));
    } catch (error) {
      console.error('Category template error:', error);
      toast.error('Failed to load category template');
    }
  };

  // Submit new rate
  const handleSubmitRate = async () => {
    if (!selectedCategory || !formData.material_name || !formData.rate) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        category: selectedCategory,
        material_name: formData.material_name,
        description: formData.description,
        unit: formData.unit,
        rate: parseFloat(formData.rate),
        gst: formData.gst,
        inclusion: formData.inclusion,
        exclusions: formData.exclusions,
        validity_days: parseInt(formData.validity_days),
      };
      
      const response = await axios.post(`${API}/vendor/rates/submit`, payload);
      
      // P0 FIX: Show explicit confirmation with rate details
      if (response.data.success) {
        toast.success(
          <div>
            <p className="font-medium">Rate Submitted Successfully!</p>
            <p className="text-sm text-stone-600">{formData.material_name}</p>
            <p className="text-sm text-stone-600">₹{formData.rate}/{formData.unit}</p>
          </div>,
          { duration: 5000 }
        );
        
        // Close panel and refresh rates
        setPanelOpen(false);
        setSelectedCategory(null);
        setCategoryTemplate(null);
        setFormData({
          material_name: '',
          description: '',
          unit: 'Sq.ft',
          rate: '',
          gst: 'extra',
          inclusion: 'material_only',
          exclusions: '',
          validity_days: '30',
        });
        
        // Refresh rates list to show the new rate immediately
        await fetchRates();
        
        // Optionally open the newly submitted rate
        if (response.data.rate) {
          setSelectedRate(response.data.rate);
          setPanelMode('view');
          setPanelOpen(true);
        }
      }
    } catch (error) {
      console.error('Rate submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit rate. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit amendment request
  const handleSubmitAmendment = async () => {
    if (!amendmentForm.reason || !amendmentForm.comment.trim()) {
      toast.error('Please select a reason and provide a comment');
      return;
    }
    
    setSubmittingAmendment(true);
    try {
      const payload = {
        reason: amendmentForm.reason,
        comment: amendmentForm.comment,
      };
      
      if (amendmentForm.reason === 'extend_validity') {
        payload.requested_validity_days = parseInt(amendmentForm.requested_validity_days);
      }
      if (amendmentForm.reason === 'incorrect_rate') {
        payload.proposed_rate = parseFloat(amendmentForm.proposed_rate);
      }
      if (amendmentForm.reason === 'incorrect_description') {
        payload.proposed_description = amendmentForm.proposed_description;
      }
      
      await axios.post(`${API}/vendor/rates/${selectedRate.id}/request-amendment`, payload);
      toast.success('Amendment request submitted');
      setPanelOpen(false);
      fetchRates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit amendment');
    } finally {
      setSubmittingAmendment(false);
    }
  };

  // Accept admin amendment
  const handleAcceptAmendment = async (requestId) => {
    try {
      await axios.post(`${API}/vendor/amendment-requests/${requestId}/accept`);
      toast.success('Changes accepted. Rate is now approved.');
      fetchRates();
      // Refresh the selected rate
      if (selectedRate) {
        const response = await axios.get(`${API}/vendor/rates/${selectedRate.id}`);
        setSelectedRate(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept changes');
    }
  };

  // Upload photos
  const handlePhotoUpload = async (rateId, files) => {
    if (!files?.length) return;
    
    setUploadingPhotos(true);
    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('photos', file));
    
    try {
      await axios.post(`${API}/vendor/rates/${rateId}/photos`, formData);
      toast.success('Photos uploaded');
      fetchRates();
      if (selectedRate?.id === rateId) {
        const response = await axios.get(`${API}/vendor/rates/${rateId}`);
        setSelectedRate(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Get all categories for dropdown
  const allCategories = [
    ...(categories.standard_categories || []),
    ...(categories.custom_categories || [])
  ];

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">My Rate Submissions</h1>
            <p className="text-stone-500 mt-1">Manage your submitted rates and track their status</p>
          </div>
          <Button onClick={openCreatePanel} className="bg-stone-900 hover:bg-stone-800" data-testid="add-rate-btn">
            <Plus size={18} className="mr-2" />
            Submit New Rate
          </Button>
        </div>

        {/* Tabs for Active vs Archived */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Package size={16} />
              Active Rates ({activeRates.length})
            </TabsTrigger>
            <TabsTrigger value="archived" className="flex items-center gap-2">
              <Archive size={16} />
              Archived ({archivedRates.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <Input
              placeholder="Search rates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-rates-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <Filter size={16} className="mr-2 text-stone-400" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="amendment_requested">Amendment Requested</SelectItem>
              <SelectItem value="amended_pending_vendor_review">Pending Review</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="locked">Locked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-2xl font-bold text-stone-900">{activeRates.length}</p>
            <p className="text-sm text-stone-500">Active Rates</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-2xl font-bold text-emerald-600">{rates.filter(r => r.status === 'approved').length}</p>
            <p className="text-sm text-stone-500">Approved</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-2xl font-bold text-amber-600">{rates.filter(r => r.status === 'under_review').length}</p>
            <p className="text-sm text-stone-500">Under Review</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-2xl font-bold text-orange-600">{rates.filter(r => r.status === 'amended_pending_vendor_review').length}</p>
            <p className="text-sm text-stone-500">Needs Your Review</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-2xl font-bold text-purple-600">{rates.filter(r => r.status === 'locked').length}</p>
            <p className="text-sm text-stone-500">BOQ Ready</p>
          </div>
        </div>

        {/* Rates List - Notion Style */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="animate-spin mx-auto text-stone-400" size={32} />
              <p className="text-stone-500 mt-2">Loading rates...</p>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="mx-auto text-stone-300 mb-4" size={48} />
              <p className="text-stone-500">No rates found</p>
              <Button onClick={openCreatePanel} variant="outline" className="mt-4">
                <Plus size={16} className="mr-2" />
                Submit Your First Rate
              </Button>
            </div>
          ) : (
            filteredCategories.map((category) => {
              const categoryRates = groupedRates[category].filter(rate => {
                const matchesSearch = !searchQuery || 
                  rate.material_name?.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesStatus = statusFilter === 'all' || rate.status === statusFilter;
                return matchesSearch && matchesStatus;
              });

              return (
                <div key={category} className="border-b border-stone-100 last:border-0">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-stone-50 transition-colors"
                    data-testid={`category-header-${category}`}
                  >
                    <div className="flex items-center gap-3">
                      {expandedCategories.has(category) ? (
                        <ChevronDown size={20} className="text-stone-400" />
                      ) : (
                        <ChevronRight size={20} className="text-stone-400" />
                      )}
                      <Tag size={18} className="text-blue-500" />
                      <span className="font-medium text-stone-900">{category.replace(/_/g, ' ')}</span>
                      <span className="text-sm text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">
                        {categoryRates.length}
                      </span>
                    </div>
                  </button>

                  {/* Rates in Category */}
                  {expandedCategories.has(category) && (
                    <div className="border-t border-stone-100">
                      {categoryRates.map((rate) => {
                        const expiry = getExpiryInfo(rate.validity_end);
                        const hasAdminComment = rate.admin_notes || rate.admin_comments;
                        const needsReview = rate.status === 'amended_pending_vendor_review';
                        
                        return (
                          <div
                            key={rate.id}
                            onClick={() => openRateDetail(rate)}
                            className={`flex items-center justify-between px-4 py-3 hover:bg-stone-50 cursor-pointer border-b border-stone-50 last:border-0 ${
                              needsReview ? 'bg-orange-50 hover:bg-orange-100' : ''
                            }`}
                            data-testid={`rate-row-${rate.id}`}
                          >
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center flex-shrink-0">
                                <IndianRupee size={18} className="text-blue-600" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-stone-900 truncate">{rate.material_name}</p>
                                  {getStatusBadge(rate.status)}
                                  {expiry.isExpired && rate.status !== 'expired' && (
                                    <Badge variant="destructive" className="text-xs">Expired</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-sm text-stone-500">
                                  <span>₹{rate.rate?.toLocaleString()} / {rate.unit}</span>
                                  <span>•</span>
                                  <span className={expiry.isExpired ? 'text-red-500' : expiry.isExpiringSoon ? 'text-amber-500' : ''}>
                                    {expiry.isExpired ? 'Expired' : `${expiry.daysRemaining} days left`}
                                  </span>
                                  {hasAdminComment && (
                                    <>
                                      <span>•</span>
                                      <span className="text-blue-600 flex items-center gap-1">
                                        <MessageSquare size={12} />
                                        Admin Comment
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Quick Actions */}
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              {needsReview && (
                                <Badge className="bg-orange-500 text-white animate-pulse">
                                  <Bell size={12} className="mr-1" />
                                  Review Required
                                </Badge>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleStartChatAboutRate(rate)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title={`Chat with Admin about ${rate.material_name}`}
                                data-testid={`chat-rate-${rate.id}`}
                              >
                                <MessageCircle size={16} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openRateDetail(rate)}
                                data-testid={`view-rate-${rate.id}`}
                              >
                                <Eye size={16} className="mr-1" />
                                View
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Side Panel */}
      <SidePanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        onMinimize={() => setPanelMinimized(!panelMinimized)}
        minimized={panelMinimized}
        title={
          panelMode === 'create' ? 'Submit New Rate' : 
          panelMode === 'amendment' ? 'Request Amendment' :
          selectedRate?.material_name || 'Rate Details'
        }
        subtitle={panelMode === 'view' ? selectedRate?.category?.replace(/_/g, ' ') : undefined}
      >
        {/* VIEW MODE */}
        {panelMode === 'view' && selectedRate && (
          <div className="space-y-6">
            {loadingRate ? (
              <div className="py-8 text-center">
                <Loader2 className="animate-spin mx-auto text-stone-400" size={32} />
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                    <IndianRupee size={32} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-stone-900">{selectedRate.material_name}</h3>
                    <p className="text-stone-500">{selectedRate.category?.replace(/_/g, ' ')}</p>
                    <div className="mt-2">{getStatusBadge(selectedRate.status)}</div>
                  </div>
                </div>

                {/* Admin Amendment Notice - PROMINENT */}
                {(selectedRate.status === 'amended_pending_vendor_review' || selectedRate.status === 'modified_by_admin') && (
                  <div className="p-4 bg-orange-100 rounded-xl border border-orange-200">
                    <div className="flex items-start gap-3">
                      <Bell className="text-orange-600 mt-0.5" size={20} />
                      <div className="flex-1">
                        <p className="font-semibold text-orange-800">Admin has amended this rate</p>
                        <p className="text-sm text-orange-700 mt-1">
                          Please review the changes below and accept to activate this rate.
                        </p>
                        {selectedRate.admin_notes && (
                          <div className="mt-2 p-2 bg-white rounded-lg">
                            <p className="text-sm text-stone-600">
                              <strong>Admin Note:</strong> {selectedRate.admin_notes}
                            </p>
                          </div>
                        )}
                        
                        {/* PREVIEW: Original vs Amended - Immediate visibility without deployment */}
                        {selectedRate.original_values && (
                          <div className="mt-4 grid grid-cols-2 gap-3">
                            <div className="p-3 bg-white rounded-lg border border-stone-200">
                              <p className="text-xs text-stone-500 uppercase mb-2 font-semibold">Your Original</p>
                              <div className="space-y-1 text-sm">
                                <p><strong>Rate:</strong> ₹{selectedRate.original_values.rate?.toLocaleString()}</p>
                                <p><strong>Unit:</strong> {selectedRate.original_values.unit || selectedRate.unit}</p>
                                {selectedRate.original_values.description && (
                                  <p><strong>Desc:</strong> {selectedRate.original_values.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="p-3 bg-orange-50 rounded-lg border-2 border-orange-300">
                              <p className="text-xs text-orange-600 uppercase mb-2 font-semibold">Admin Amended</p>
                              <div className="space-y-1 text-sm">
                                <p className={selectedRate.rate !== selectedRate.original_values.rate ? 'text-orange-700 font-bold' : ''}>
                                  <strong>Rate:</strong> ₹{selectedRate.rate?.toLocaleString()}
                                  {selectedRate.rate !== selectedRate.original_values.rate && ' ⬅ Changed'}
                                </p>
                                <p><strong>Unit:</strong> {selectedRate.unit}</p>
                                {selectedRate.description !== selectedRate.original_values.description && (
                                  <p className="text-orange-700 font-bold">
                                    <strong>Desc:</strong> {selectedRate.description} ⬅ Changed
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex gap-2 mt-4">
                          <Button 
                            className="bg-orange-600 hover:bg-orange-700"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/vendor/rates/${selectedRate.id}/accept-changes`);
                                toast.success('Changes accepted. Rate is now approved.');
                                fetchRates();
                                const response = await axios.get(`${API}/vendor/rates/${selectedRate.id}`);
                                setSelectedRate(response.data);
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to accept changes');
                              }
                            }}
                          >
                            <Check size={16} className="mr-2" />
                            Accept Changes
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => openAmendmentPanel(selectedRate)}
                          >
                            <MessageSquare size={16} className="mr-2" />
                            Request Clarification
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin Comments - Inline Display */}
                {(selectedRate.admin_notes || selectedRate.admin_comments) && 
                 selectedRate.status !== 'amended_pending_vendor_review' && 
                 selectedRate.status !== 'modified_by_admin' && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-2">
                      <MessageSquare size={16} className="text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">Admin Comment</p>
                        <p className="text-sm text-blue-700">{selectedRate.admin_notes || selectedRate.admin_comments}</p>
                      </div>
                    </div>
                  </div>
                )}

                <SidePanelDivider />

                {/* Original vs Amended Comparison (for approved amendments) */}
                {selectedRate.amendment_requests?.some(r => r.status === 'approved') && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-stone-900 flex items-center gap-2">
                      <RefreshCw size={16} />
                      Changes Made by Admin
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-stone-50 rounded-lg">
                        <p className="text-xs text-stone-500 uppercase mb-2">Original Submission</p>
                        {selectedRate.amendment_requests?.find(r => r.status === 'approved')?.original_rate_data && (
                          <div className="space-y-1 text-sm">
                            <p><strong>Rate:</strong> ₹{selectedRate.amendment_requests.find(r => r.status === 'approved').original_rate_data.rate?.toLocaleString()}</p>
                            <p><strong>Unit:</strong> {selectedRate.amendment_requests.find(r => r.status === 'approved').original_rate_data.unit}</p>
                          </div>
                        )}
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-xs text-emerald-600 uppercase mb-2">Admin Amended (Preview)</p>
                        <div className="space-y-1 text-sm">
                          <p><strong>Rate:</strong> ₹{selectedRate.rate?.toLocaleString()}</p>
                          <p><strong>Unit:</strong> {selectedRate.unit}</p>
                        </div>
                      </div>
                    </div>
                    <SidePanelDivider />
                  </div>
                )}

                {/* Rate Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-stone-500 uppercase">Rate</p>
                    <p className="font-semibold text-lg text-stone-900">₹{selectedRate.rate?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-stone-500 uppercase">Unit</p>
                    <p className="font-medium text-stone-900">{selectedRate.unit}</p>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-stone-500 uppercase">GST</p>
                    <p className="font-medium text-stone-900">{selectedRate.gst === 'included' ? 'Included' : 'Extra'}</p>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-stone-500 uppercase">Validity</p>
                    <p className={`font-medium ${getExpiryInfo(selectedRate.validity_end).isExpired ? 'text-red-600' : 'text-stone-900'}`}>
                      {getExpiryInfo(selectedRate.validity_end).isExpired 
                        ? 'Expired' 
                        : `${getExpiryInfo(selectedRate.validity_end).daysRemaining} days remaining`}
                    </p>
                  </div>
                </div>

                {selectedRate.description && (
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-stone-500 uppercase mb-1">Description</p>
                    <p className="text-stone-900">{selectedRate.description}</p>
                  </div>
                )}

                {selectedRate.exclusions && (
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-stone-500 uppercase mb-1">Exclusions</p>
                    <p className="text-stone-900">{selectedRate.exclusions}</p>
                  </div>
                )}

                {/* Photos */}
                {selectedRate.photos?.length > 0 && (
                  <div>
                    <p className="text-xs text-stone-500 uppercase mb-2">Photos</p>
                    <div className="flex gap-2">
                      {selectedRate.photos.map((photo, idx) => (
                        <img key={idx} src={photo} alt={`Photo ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg" />
                      ))}
                    </div>
                  </div>
                )}

                {/* Amendment History */}
                {selectedRate.amendment_requests?.length > 0 && (
                  <>
                    <SidePanelDivider />
                    <div>
                      <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                        <MessageSquare size={16} />
                        Amendment History
                      </h4>
                      <div className="space-y-2">
                        {selectedRate.amendment_requests.map((req, idx) => (
                          <div key={idx} className="p-3 bg-stone-50 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{req.reason?.replace(/_/g, ' ')}</span>
                              <Badge variant={req.status === 'approved' ? 'default' : req.status === 'rejected' ? 'destructive' : 'secondary'}>
                                {req.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-stone-600">{req.comment}</p>
                            {req.admin_notes && (
                              <p className="text-sm text-blue-600 mt-1">
                                <strong>Admin:</strong> {req.admin_notes}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <SidePanelDivider />

                {/* Actions */}
                <SidePanelActions>
                  {/* Request Amendment for expired/rejected rates */}
                  {(getExpiryInfo(selectedRate.validity_end).isExpired || selectedRate.status === 'rejected') && 
                   selectedRate.status !== 'amendment_requested' && (
                    <Button 
                      className="flex-1 bg-amber-500 hover:bg-amber-600"
                      onClick={() => openAmendmentPanel(selectedRate)}
                    >
                      <MessageSquare size={16} className="mr-2" />
                      Request Amendment
                    </Button>
                  )}
                  
                  {/* Photo Upload */}
                  {selectedRate.status !== 'locked' && !getExpiryInfo(selectedRate.validity_end).isExpired && (
                    <label className="flex-1">
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        multiple
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(selectedRate.id, e.target.files)}
                        disabled={uploadingPhotos || (selectedRate.photos?.length >= 3)}
                      />
                      <Button variant="outline" className="w-full" asChild disabled={selectedRate.photos?.length >= 3}>
                        <span>
                          {uploadingPhotos ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                          Add Photos ({selectedRate.photos?.length || 0}/3)
                        </span>
                      </Button>
                    </label>
                  )}
                </SidePanelActions>
              </>
            )}
          </div>
        )}

        {/* CREATE MODE */}
        {panelMode === 'create' && (
          <div className="space-y-6">
            {!selectedCategory ? (
              <>
                <p className="text-stone-600">Select a category to continue:</p>
                
                {/* Category Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                  <Input
                    placeholder="Search categories..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="category-search"
                  />
                </div>
                
                {/* Grouped Categories Display */}
                <div className="space-y-4 max-h-[450px] overflow-y-auto">
                  {categories.category_groups?.map((groupName) => {
                    const groupCategories = (categories.grouped_categories?.[groupName] || [])
                      .filter(cat => 
                        !searchQuery || 
                        cat.name.toLowerCase().includes(searchQuery.toLowerCase())
                      );
                    
                    if (groupCategories.length === 0) return null;
                    
                    return (
                      <div key={groupName} className="border border-stone-200 rounded-lg overflow-hidden">
                        <div className="bg-stone-100 px-3 py-2 border-b border-stone-200">
                          <p className="font-medium text-stone-700 text-sm">{groupName}</p>
                        </div>
                        <div className="divide-y divide-stone-100">
                          {groupCategories.map((cat) => (
                            <button
                              key={cat.id}
                              onClick={() => handleCategorySelect(cat.id)}
                              className="w-full p-3 text-left hover:bg-stone-50 transition-colors flex items-center justify-between"
                              data-testid={`category-${cat.id}`}
                            >
                              <div>
                                <p className="font-medium text-stone-900">{cat.name}</p>
                                <p className="text-xs text-stone-500">
                                  Units: {cat.allowed_units?.slice(0, 3).join(', ')}
                                  {cat.allowed_units?.length > 3 && '...'}
                                </p>
                              </div>
                              <ChevronRight size={16} className="text-stone-400" />
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Custom Categories */}
                  {categories.custom_categories?.length > 0 && (
                    <div className="border border-stone-200 rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-3 py-2 border-b border-stone-200">
                        <p className="font-medium text-blue-700 text-sm">Custom Categories</p>
                      </div>
                      <div className="divide-y divide-stone-100">
                        {categories.custom_categories
                          .filter(cat => 
                            !searchQuery || 
                            cat.name.toLowerCase().includes(searchQuery.toLowerCase())
                          )
                          .map((cat) => (
                            <button
                              key={cat.id}
                              onClick={() => handleCategorySelect(cat.id)}
                              className="w-full p-3 text-left hover:bg-stone-50 transition-colors flex items-center justify-between"
                            >
                              <div>
                                <p className="font-medium text-stone-900">{cat.name}</p>
                                <p className="text-xs text-stone-500">Custom</p>
                              </div>
                              <ChevronRight size={16} className="text-stone-400" />
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Request New Category Link */}
                <div className="pt-4 border-t border-stone-200">
                  <p className="text-sm text-stone-500 mb-2">Cannot find your category?</p>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setRequestCategoryDialogOpen(true)}
                  >
                    <Plus size={16} className="mr-2" />
                    Request New Category
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Button variant="ghost" size="sm" onClick={() => {
                    setSelectedCategory(null);
                    setCategoryTemplate(null);
                    setSearchQuery('');
                  }}>
                    ← Back
                  </Button>
                  {categoryTemplate && (
                    <Badge className="bg-stone-100 text-stone-700">
                      {categoryTemplate.category_group || 'Category'}
                    </Badge>
                  )}
                </div>
                
                {categoryTemplate && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                    <p className="font-medium text-blue-900">{categoryTemplate.category_name}</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Allowed units: {categoryTemplate.allowed_units?.join(', ')}
                    </p>
                    {/* Rate Benchmarks */}
                    {categoryTemplate.rate_benchmarks && Object.keys(categoryTemplate.rate_benchmarks).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <p className="text-xs font-medium text-blue-800 mb-1">💡 Market Rate Benchmarks:</p>
                        <div className="space-y-1">
                          {Object.entries(categoryTemplate.rate_benchmarks).map(([unit, benchmark]) => (
                            <p key={unit} className="text-xs text-blue-700">
                              {unit}: {benchmark.display}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <Label>Material/Service Name *</Label>
                    <Input
                      value={formData.material_name}
                      onChange={(e) => setFormData({...formData, material_name: e.target.value})}
                      placeholder="e.g., 18mm BWP Plywood"
                      data-testid="rate-material-name"
                    />
                    {/* Template suggestions if available */}
                    {categoryTemplate?.template_items?.length > 0 && !formData.material_name && (
                      <div className="mt-2">
                        <p className="text-xs text-stone-500 mb-1">Suggestions:</p>
                        <div className="flex flex-wrap gap-1">
                          {categoryTemplate.template_items.slice(0, 4).map((item, idx) => (
                            <button
                              key={idx}
                              onClick={() => setFormData({
                                ...formData, 
                                material_name: item.name,
                                description: item.description || '',
                                unit: item.default_unit || formData.unit
                              })}
                              className="text-xs px-2 py-1 bg-stone-100 hover:bg-stone-200 rounded transition-colors"
                            >
                              {item.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Description</Label>
                      {categoryTemplate?.ai_assisted && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleGenerateAIDescription}
                          disabled={generatingAIDescription || !formData.material_name}
                          className="h-7 text-xs"
                          data-testid="ai-generate-btn"
                        >
                          {generatingAIDescription ? (
                            <Loader2 size={12} className="mr-1 animate-spin" />
                          ) : (
                            <Sparkles size={12} className="mr-1" />
                          )}
                          ✨ Generate Description
                        </Button>
                      )}
                    </div>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Specifications, brand, etc. Use the 'Generate Description' button for AI assistance."
                      rows={4}
                      data-testid="rate-description"
                    />
                    {/* Description hints if available */}
                    {categoryTemplate?.description_hints && (
                      <p className="text-xs text-stone-500 mt-1">
                        Include: {Object.values(categoryTemplate.description_hints.prompts || {}).join(', ')}
                      </p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Unit *</Label>
                      <Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}>
                        <SelectTrigger data-testid="rate-unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(categoryTemplate?.allowed_units || UNIT_OPTIONS).map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Rate (₹) *</Label>
                      <Input
                        type="number"
                        value={formData.rate}
                        onChange={(e) => setFormData({...formData, rate: e.target.value})}
                        placeholder="0.00"
                        data-testid="rate-amount"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>GST</Label>
                      <Select value={formData.gst} onValueChange={(v) => setFormData({...formData, gst: v})}>
                        <SelectTrigger data-testid="rate-gst">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="included">Included</SelectItem>
                          <SelectItem value="extra">Extra</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Validity</Label>
                      <Select value={formData.validity_days} onValueChange={(v) => setFormData({...formData, validity_days: v})}>
                        <SelectTrigger data-testid="rate-validity">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VALIDITY_OPTIONS.map(d => (
                            <SelectItem key={d} value={d.toString()}>{d} days</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Exclusions (if any)</Label>
                    <Textarea
                      value={formData.exclusions}
                      onChange={(e) => setFormData({...formData, exclusions: e.target.value})}
                      placeholder="What's not included..."
                      rows={2}
                      data-testid="rate-exclusions"
                    />
                  </div>
                </div>
                
                <SidePanelDivider />
                
                <SidePanelActions>
                  <Button 
                    className="flex-1 bg-stone-900 hover:bg-stone-800"
                    onClick={handleSubmitRate}
                    disabled={submitting}
                    data-testid="submit-rate-btn"
                  >
                    {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
                    Submit Rate
                  </Button>
                </SidePanelActions>
              </>
            )}
          </div>
        )}

        {/* AMENDMENT MODE */}
        {panelMode === 'amendment' && selectedRate && (
          <div className="space-y-6">
            {/* Current Rate Info */}
            <div className="p-3 bg-stone-50 rounded-lg">
              <p className="font-medium">{selectedRate.material_name}</p>
              <p className="text-sm text-stone-500">Current Rate: ₹{selectedRate.rate?.toLocaleString()} / {selectedRate.unit}</p>
            </div>
            
            <div>
              <Label>Reason for Amendment *</Label>
              <Select value={amendmentForm.reason} onValueChange={(v) => setAmendmentForm({...amendmentForm, reason: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  {AMENDMENT_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {amendmentForm.reason === 'extend_validity' && (
              <div>
                <Label>Requested Validity (days)</Label>
                <Select value={amendmentForm.requested_validity_days} onValueChange={(v) => setAmendmentForm({...amendmentForm, requested_validity_days: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VALIDITY_OPTIONS.map(d => (
                      <SelectItem key={d} value={d.toString()}>{d} days</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {amendmentForm.reason === 'incorrect_rate' && (
              <div>
                <Label>Proposed Correct Rate (₹)</Label>
                <Input
                  type="number"
                  value={amendmentForm.proposed_rate}
                  onChange={(e) => setAmendmentForm({...amendmentForm, proposed_rate: e.target.value})}
                />
              </div>
            )}
            
            {amendmentForm.reason === 'incorrect_description' && (
              <div>
                <Label>Proposed Description</Label>
                <Textarea
                  value={amendmentForm.proposed_description}
                  onChange={(e) => setAmendmentForm({...amendmentForm, proposed_description: e.target.value})}
                  rows={3}
                />
              </div>
            )}
            
            <div>
              <Label>Explanation / Comment *</Label>
              <Textarea
                value={amendmentForm.comment}
                onChange={(e) => setAmendmentForm({...amendmentForm, comment: e.target.value})}
                placeholder="Explain why this amendment is needed..."
                rows={3}
              />
            </div>
            
            <SidePanelDivider />
            
            <SidePanelActions>
              <Button 
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                onClick={handleSubmitAmendment}
                disabled={submittingAmendment || !amendmentForm.reason || !amendmentForm.comment.trim()}
              >
                {submittingAmendment ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                Submit Request
              </Button>
              <Button variant="outline" onClick={() => setPanelOpen(false)}>
                Cancel
              </Button>
            </SidePanelActions>
          </div>
        )}
      </SidePanel>

      {/* Request New Category Dialog */}
      <Dialog open={requestCategoryDialogOpen} onOpenChange={(open) => {
        setRequestCategoryDialogOpen(open);
        if (!open) {
          setCategorySuggestions(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="text-blue-600" size={20} />
              Request New Category
            </DialogTitle>
            <DialogDescription>
              Submit a request for a new category. Use AI to help generate suggestions.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Category Name with AI Button */}
            <div>
              <Label className="text-stone-700">Category Name *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={requestCategoryForm.name}
                  onChange={(e) => setRequestCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Custom Woodwork, Acoustic Panels"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateCategorySuggestions}
                  disabled={generatingCategorySuggestions || !requestCategoryForm.name}
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                >
                  {generatingCategorySuggestions ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <>
                      <Sparkles size={14} className="mr-1" />
                      AI Suggest
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* AI Suggestions Panel */}
            {categorySuggestions && categorySuggestions.length > 0 && (
              <div className="border border-purple-200 rounded-lg p-3 bg-purple-50">
                <p className="text-sm font-medium text-purple-800 mb-2 flex items-center gap-1">
                  <Sparkles size={14} />
                  AI Suggestions
                </p>
                <div className="space-y-2">
                  {categorySuggestions.map((suggestion, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white rounded p-2 border border-purple-100 hover:border-purple-300 cursor-pointer transition-colors"
                      onClick={() => applyCategorySuggestion(suggestion)}
                    >
                      <p className="font-medium text-stone-800">{suggestion.name}</p>
                      {suggestion.reason && (
                        <p className="text-xs text-stone-500 mt-1 line-clamp-2">{suggestion.reason}</p>
                      )}
                      <Button size="sm" variant="ghost" className="text-purple-600 text-xs mt-1 p-0 h-auto">
                        Apply This Suggestion →
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Why is this category needed? with AI Generate */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-stone-700">Why is this category needed? *</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    if (!requestCategoryForm.name) {
                      toast.error('Please enter a category name first');
                      return;
                    }
                    setGeneratingCategorySuggestions(true);
                    try {
                      const response = await axios.post(`${API}/ai/generate-category-reason`, {
                        category_name: requestCategoryForm.name,
                        trade_description: requestCategoryForm.trade_description
                      });
                      if (response.data.reason) {
                        setRequestCategoryForm(prev => ({ ...prev, reason: response.data.reason }));
                        toast.success('AI generated justification!');
                      }
                    } catch (error) {
                      // Fallback: Generate a basic reason
                      const fallbackReason = `The "${requestCategoryForm.name}" category is required to accurately quote and track specialized work that doesn't fit existing categories. This will enable proper cost estimation and vendor rate management for this trade.`;
                      setRequestCategoryForm(prev => ({ ...prev, reason: fallbackReason }));
                      toast.success('Generated justification');
                    } finally {
                      setGeneratingCategorySuggestions(false);
                    }
                  }}
                  disabled={generatingCategorySuggestions || !requestCategoryForm.name}
                  className="text-purple-600 text-xs"
                >
                  <Sparkles size={12} className="mr-1" />
                  AI Generate
                </Button>
              </div>
              <Textarea
                value={requestCategoryForm.reason}
                onChange={(e) => setRequestCategoryForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Explain why this category is needed for your work..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label className="text-stone-700">Suggested Units (optional)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {UNIT_OPTIONS.slice(0, 10).map(unit => (
                  <Button
                    key={unit}
                    type="button"
                    variant={requestCategoryForm.suggested_units.includes(unit) ? "default" : "outline"}
                    size="sm"
                    className={requestCategoryForm.suggested_units.includes(unit) ? "bg-blue-600" : ""}
                    onClick={() => {
                      setRequestCategoryForm(prev => ({
                        ...prev,
                        suggested_units: prev.suggested_units.includes(unit)
                          ? prev.suggested_units.filter(u => u !== unit)
                          : [...prev.suggested_units, unit]
                      }));
                    }}
                  >
                    {unit}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-stone-700">Trade Description (optional)</Label>
              <Textarea
                value={requestCategoryForm.trade_description}
                onChange={(e) => setRequestCategoryForm(prev => ({ ...prev, trade_description: e.target.value }))}
                placeholder="Describe what this trade/category typically includes..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setRequestCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitCategoryRequest}
              disabled={submittingCategoryRequest}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submittingCategoryRequest ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
